import { useState, useEffect, type RefObject } from 'react';

/**
 * Hook to track horizontal scroll position and determine whether
 * left/right fade overlays should be shown.
 *
 * Used by horizontally-scrollable containers (CategoryPills, ProductsFilterBar)
 * to display gradient fade indicators at the edges.
 *
 * Rebinds when `scrollRef.current` changes identity (e.g. ProductsFilterBar
 * remounts the scroll container on the Products/Brands toggle). A ref object
 * alone is not a reliable effect dependency for that remount.
 */
export const useScrollFade = (scrollRef: RefObject<HTMLElement | null>) => {
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [element, setElement] = useState<HTMLElement | null>(null);

    // Sync the live DOM node into state so we re-attach after remounts.
    // Same-node updates are a React no-op.
    useEffect(() => {
        setElement(scrollRef.current);
    });

    useEffect(() => {
        if (!element) {
            setShowLeftFade(false);
            setShowRightFade(false);
            setIsOverflowing(false);
            return;
        }

        const updateFadeState = () => {
            const { scrollLeft, scrollWidth, clientWidth } = element;
            const isAtStart = scrollLeft <= 1; // Small threshold for rounding
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;
            const overflowing = scrollWidth > clientWidth + 1;

            setShowLeftFade(overflowing && !isAtStart);
            setShowRightFade(overflowing && !isAtEnd);
            setIsOverflowing(overflowing);
        };

        // Initial check
        updateFadeState();

        // Listen to scroll events
        element.addEventListener('scroll', updateFadeState, { passive: true });

        // Listen to resize in case content changes
        const resizeObserver = new ResizeObserver(updateFadeState);
        resizeObserver.observe(element);

        return () => {
            element.removeEventListener('scroll', updateFadeState);
            resizeObserver.disconnect();
        };
    }, [element]);

    return { showLeftFade, showRightFade, isOverflowing };
};
